import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type ElementType,
} from "react";
import { StaffCommunicationLogs } from "./StaffCommunicationLogs";
import {
  useCommunicationList,
  useCommunicationStats,
  useRetryCommunication,
} from "@/hooks/useCommunications";
import type {
  CommunicationListParams,
  CommunicationMedium,
  CommunicationProvider,
  CommunicationRecord,
  CommunicationStatus,
} from "@/lib/communications-api";
import { pingAPI } from "@/lib/ping-api";
import type { User } from "@/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  BarChart3,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Globe,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  Radio,
  RotateCw,
  Search,
  Smartphone,
  X,
} from "lucide-react";

const MEDIUM_ICONS: Record<CommunicationMedium, ElementType> = {
  whatsapp: MessageCircle,
  email: Mail,
  sms: Smartphone,
  phone: Phone,
  push_notification: Bell,
  web_push_notification: Globe,
};

const MEDIUM_LABELS: Record<CommunicationMedium, string> = {
  whatsapp: "WhatsApp",
  email: "Email",
  sms: "SMS",
  phone: "Phone",
  push_notification: "Push",
  web_push_notification: "Web Push",
};

const STATUS_VARIANTS: Record<
  CommunicationStatus,
  "success" | "critical" | "warning" | "neutral"
> = {
  sent: "success",
  failed: "critical",
  pending: "warning",
  delivered: "neutral",
  bounced: "warning",
};

const MEDIUM_OPTIONS: CommunicationMedium[] = [
  "whatsapp",
  "email",
  "sms",
  "phone",
  "push_notification",
  "web_push_notification",
];

const STATUS_OPTIONS: CommunicationStatus[] = [
  "pending",
  "sent",
  "failed",
  "delivered",
  "bounced",
];

const PROVIDER_OPTIONS: { value: CommunicationProvider; label: string }[] = [
  { value: "onesignal", label: "OneSignal" },
  { value: "msg91", label: "MSG91" },
  { value: "superfone", label: "Superfone" },
  { value: "aisensy", label: "AiSensy" },
];

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getInitials = (firstName?: string | null, lastName?: string | null) =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "P";

export interface PatientCommunicationTarget {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
  zarity_user_id?: string | null;
}

interface PatientCommunicationLogsPanelProps {
  lockedPatient?: PatientCommunicationTarget | null;
}

export const PatientCommunicationLogsPanel = ({
  lockedPatient,
}: PatientCommunicationLogsPanelProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [patients, setPatients] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] =
    useState<PatientCommunicationTarget | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [commToRetry, setCommToRetry] = useState<CommunicationRecord | null>(
    null
  );
  const [filterMedium, setFilterMedium] = useState<CommunicationMedium | "">(
    ""
  );
  const [filterStatus, setFilterStatus] = useState<CommunicationStatus | "">(
    ""
  );
  const [filterProvider, setFilterProvider] = useState<
    CommunicationProvider | ""
  >("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const effectiveSelectedPatient = lockedPatient ?? selectedPatient;
  const isPatientLocked = Boolean(lockedPatient?.id);

  const listParams = useMemo(
    (): CommunicationListParams => ({
      user_id: effectiveSelectedPatient?.id || "",
      ...(filterMedium && { medium: filterMedium }),
      ...(filterStatus && { status: filterStatus }),
      ...(filterProvider && { provider: filterProvider }),
      ...(filterStartDate && { start_date: filterStartDate }),
      ...(filterEndDate && { end_date: filterEndDate }),
      page: currentPage,
      page_size: pageSize,
    }),
    [
      effectiveSelectedPatient?.id,
      filterMedium,
      filterStatus,
      filterProvider,
      filterStartDate,
      filterEndDate,
      currentPage,
    ]
  );

  const {
    data: commsData,
    isLoading: isLoadingComms,
    error: commsError,
  } = useCommunicationList(listParams);
  const { data: statsData, isLoading: isLoadingStats } = useCommunicationStats(
    effectiveSelectedPatient?.id || ""
  );

  const retryMutation = useRetryCommunication();

  useEffect(() => {
    if (isPatientLocked) {
      setPatients([]);
      setSearchQuery("");
      return;
    }

    if (!searchQuery.trim()) {
      setPatients([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await pingAPI.users.searchPatients(searchQuery);
        setPatients(result || []);
      } catch {
        setPatients([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [isPatientLocked, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterMedium,
    filterStatus,
    filterProvider,
    filterStartDate,
    filterEndDate,
  ]);

  useEffect(() => {
    setExpandedRow(null);
    setCurrentPage(1);
    setFilterMedium("");
    setFilterStatus("");
    setFilterProvider("");
    setFilterStartDate("");
    setFilterEndDate("");
  }, [effectiveSelectedPatient?.id]);

  const totalPages = commsData ? Math.ceil(commsData.count / pageSize) : 0;

  const clearFilters = () => {
    setFilterMedium("");
    setFilterStatus("");
    setFilterProvider("");
    setFilterStartDate("");
    setFilterEndDate("");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    Boolean(filterMedium) ||
    Boolean(filterStatus) ||
    Boolean(filterProvider) ||
    Boolean(filterStartDate) ||
    Boolean(filterEndDate);

  const handleSelectPatient = (patient: User) => {
    setSelectedPatient(patient);
    setSearchQuery("");
    setPatients([]);
    setExpandedRow(null);
    clearFilters();
  };

  const handleChangePatient = () => {
    if (isPatientLocked) {
      return;
    }

    setSelectedPatient(null);
    setSearchQuery("");
    setPatients([]);
    setExpandedRow(null);
    clearFilters();
  };

  return (
    <div className="flex flex-col gap-3">
      {!effectiveSelectedPatient && !isPatientLocked ? (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
          <Input
            placeholder="Search patient by name..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="pl-9"
          />
        </div>
      ) : effectiveSelectedPatient ? (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted z-caption font-medium text-foreground">
            {getInitials(
              effectiveSelectedPatient.first_name,
              effectiveSelectedPatient.last_name
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate z-body-md text-foreground">
              {`${effectiveSelectedPatient.first_name || ""} ${effectiveSelectedPatient.last_name || ""}`.trim() ||
                "Selected patient"}
            </p>
            <p className="truncate z-caption text-muted-foreground z-numeric">
              {effectiveSelectedPatient.phone_number || "—"}
              {effectiveSelectedPatient.zarity_user_id &&
                ` \u00B7 ${effectiveSelectedPatient.zarity_user_id}`}
            </p>
          </div>
          {!isPatientLocked && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleChangePatient}
              className="gap-1.5 z-button"
            >
              <X className="h-3.5 w-3.5" />
              Change Patient
            </Button>
          )}
        </div>
      ) : null}

      {!effectiveSelectedPatient && !isPatientLocked && searchQuery.trim() && (
        <div className="max-h-60 overflow-auto rounded-xl border border-border">
          {isSearching ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">
                Searching...
              </span>
            </div>
          ) : patients.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No patients found
            </div>
          ) : (
            patients.map((patient) => (
              <button
                key={patient.id}
                className="w-full border-b px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/30"
                onClick={() => handleSelectPatient(patient)}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted/80 text-xs font-medium text-muted-foreground">
                    {getInitials(patient.first_name, patient.last_name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {patient.first_name} {patient.last_name}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {patient.phone_number}
                      {patient.zarity_user_id &&
                        ` \u00B7 ${patient.zarity_user_id}`}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {!effectiveSelectedPatient && !isPatientLocked && !searchQuery.trim() && (
        <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
          <div className="mb-4 rounded-2xl bg-muted p-6">
            <Radio className="h-12 w-12 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium">
            Search for a patient to view their communication history
          </p>
        </div>
      )}

      {effectiveSelectedPatient && (
        <>
          <div className="grid shrink-0 grid-cols-1 gap-4 md:grid-cols-3">
            {isLoadingStats ? (
              [1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-24 animate-pulse rounded-xl border border-border bg-muted/30 p-4"
                />
              ))
            ) : statsData ? (
              <>
                <div className="relative overflow-hidden rounded-xl border border-border bg-card p-4">
                  <div className="absolute left-0 right-0 top-0 h-1 bg-primary" />
                  <div className="mb-2 flex items-center gap-2">
                    <div className="rounded-lg bg-muted p-1.5">
                      <BarChart3 className="h-4 w-4 text-primary" />
                    </div>
                    <span className="z-label uppercase tracking-[0.2em] text-muted-foreground">
                      Total
                    </span>
                  </div>
                  <p className="z-h1 z-numeric text-foreground">
                    {statsData.total}
                  </p>
                </div>

                <div className="relative overflow-hidden rounded-xl border border-border bg-card p-4">
                  <div className="absolute left-0 right-0 top-0 h-1 bg-primary" />
                  <span className="z-label uppercase tracking-[0.2em] text-muted-foreground">
                    By Medium
                  </span>
                  <div className="mt-2 space-y-1">
                    {statsData.by_medium.map((item) => {
                      const Icon =
                        MEDIUM_ICONS[item.communication_medium] || Radio;
                      return (
                        <div
                          key={item.communication_medium}
                          className="flex items-center justify-between z-body"
                        >
                          <div className="flex items-center gap-1.5">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-foreground">
                              {MEDIUM_LABELS[item.communication_medium] ||
                                item.communication_medium}
                            </span>
                          </div>
                          <span className="font-medium text-foreground z-numeric">
                            {item.count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-xl border border-border bg-card p-4">
                  <div className="absolute left-0 right-0 top-0 h-1 bg-primary" />
                  <span className="z-label uppercase tracking-[0.2em] text-muted-foreground">
                    By Status
                  </span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {statsData.by_status.map((item) => (
                      <Badge
                        key={item.status}
                        variant={STATUS_VARIANTS[item.status]}
                        className="z-caption capitalize"
                      >
                        {item.status} ({item.count})
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-4">
            <Select
              value={filterMedium || "all"}
              onValueChange={(value) =>
                setFilterMedium(
                  value === "all" ? "" : (value as CommunicationMedium)
                )
              }
            >
              <SelectTrigger className="h-8 w-[140px] z-caption">
                <SelectValue placeholder="All Mediums" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mediums</SelectItem>
                {MEDIUM_OPTIONS.map((medium) => (
                  <SelectItem key={medium} value={medium}>
                    {MEDIUM_LABELS[medium]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filterStatus || "all"}
              onValueChange={(value) =>
                setFilterStatus(
                  value === "all" ? "" : (value as CommunicationStatus)
                )
              }
            >
              <SelectTrigger className="h-8 w-[140px] z-caption">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem
                    key={status}
                    value={status}
                    className="capitalize"
                  >
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filterProvider || "all"}
              onValueChange={(value) =>
                setFilterProvider(
                  value === "all" ? "" : (value as CommunicationProvider)
                )
              }
            >
              <SelectTrigger className="h-8 w-[140px] z-caption">
                <SelectValue placeholder="All Providers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                {PROVIDER_OPTIONS.map((provider) => (
                  <SelectItem key={provider.value} value={provider.value}>
                    {provider.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filterStartDate}
              onChange={(event) => setFilterStartDate(event.target.value)}
              className="h-8 w-[140px] z-caption"
              placeholder="Start date"
            />
            <Input
              type="date"
              value={filterEndDate}
              onChange={(event) => setFilterEndDate(event.target.value)}
              className="h-8 w-[140px] z-caption"
              placeholder="End date"
            />

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-8 gap-1 z-button text-muted-foreground"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            )}
          </div>

          <div className="rounded-xl border border-border">
            {isLoadingComms ? (
              <div className="flex h-full min-h-[240px] items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-3 text-sm text-muted-foreground">
                  Loading communications...
                </span>
              </div>
            ) : commsError ? (
              <div className="flex h-full min-h-[240px] flex-col items-center justify-center py-16 text-destructive">
                <div className="mb-4 rounded-full bg-destructive/10 p-4">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
                <p className="text-sm font-medium">
                  {(commsError as Error)?.message ||
                    "Failed to load communications"}
                </p>
              </div>
            ) : !commsData || commsData.results.length === 0 ? (
              <div className="flex h-full min-h-[240px] flex-col items-center justify-center py-16 text-muted-foreground">
                <div className="mb-4 rounded-full bg-muted p-4">
                  <Radio className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">No communications found</p>
                <p className="mt-1 text-xs text-muted-foreground/70">
                  Try adjusting your filters
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="w-[50px] z-label uppercase tracking-wide">
                      Medium
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide">
                      Provider
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide">
                      Template / Content
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide">
                      Recipient
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide">
                      Status
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide">
                      Date
                    </TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commsData.results.map((comm) => {
                    const MediumIcon =
                      MEDIUM_ICONS[comm.communication_medium] || Radio;
                    const isExpanded = expandedRow === comm.id;

                    return (
                      <Fragment key={comm.id}>
                        <TableRow
                          className="cursor-pointer border-border transition-colors hover:bg-muted/30"
                          onClick={() =>
                            setExpandedRow(isExpanded ? null : comm.id)
                          }
                        >
                          <TableCell>
                            <MediumIcon className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                          <TableCell className="z-body capitalize text-foreground">
                            {comm.communication_provider}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate z-body text-foreground">
                            {comm.template_name || comm.template_id || "\u2014"}
                          </TableCell>
                          <TableCell className="font-mono z-numeric z-caption text-muted-foreground">
                            {comm.recipient}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={STATUS_VARIANTS[comm.status]}
                                className="z-caption capitalize"
                              >
                                {comm.status}
                              </Badge>
                              {comm.retry_count > 0 && (
                                <Badge variant="outline" className="z-caption">
                                  {comm.retry_count}x retries
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap z-body text-muted-foreground z-numeric">
                            {formatDate(comm.created_at)}
                          </TableCell>
                          <TableCell>
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </TableCell>
                        </TableRow>

                        {isExpanded && (
                          <TableRow className="border-border">
                            <TableCell
                              colSpan={7}
                              className="bg-muted/30 px-6 py-4"
                            >
                              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                <div>
                                  <span className="font-medium text-muted-foreground">
                                    External ID:
                                  </span>{" "}
                                  <span className="font-mono text-xs text-foreground">
                                    {comm.external_id || "\u2014"}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium text-muted-foreground">
                                    Template ID:
                                  </span>{" "}
                                  <span className="font-mono text-xs text-foreground">
                                    {comm.template_id || "\u2014"}
                                  </span>
                                </div>

                                {comm.error_code && (
                                  <div>
                                    <span className="font-medium text-muted-foreground">
                                      Error Code:
                                    </span>{" "}
                                    <span className="font-mono text-xs text-destructive">
                                      {comm.error_code}
                                    </span>
                                  </div>
                                )}

                                {comm.error_message && (
                                  <div className="col-span-2">
                                    <span className="font-medium text-muted-foreground">
                                      Error Message:
                                    </span>{" "}
                                    <span className="text-xs text-destructive">
                                      {comm.error_message}
                                    </span>
                                  </div>
                                )}

                                {comm.metadata &&
                                  Object.keys(comm.metadata).length > 0 && (
                                    <div className="col-span-2">
                                      <span className="font-medium text-muted-foreground">
                                        Metadata:
                                      </span>
                                      <pre className="mt-2 max-h-32 overflow-auto rounded-lg border border-border bg-muted p-3 text-xs text-muted-foreground">
                                        {JSON.stringify(comm.metadata, null, 2)}
                                      </pre>
                                    </div>
                                  )}

                                {comm.http_status_code && (
                                  <div>
                                    <span className="font-medium text-muted-foreground">
                                      HTTP Status:
                                    </span>{" "}
                                    <span
                                      className={cn(
                                        "font-mono text-xs",
                                        comm.http_status_code >= 200 &&
                                          comm.http_status_code < 300
                                          ? "text-success"
                                          : "text-destructive"
                                      )}
                                    >
                                      {comm.http_status_code}
                                    </span>
                                  </div>
                                )}

                                {comm.response_time_ms && (
                                  <div>
                                    <span className="font-medium text-muted-foreground">
                                      Response Time:
                                    </span>{" "}
                                    <span className="font-mono text-xs text-foreground">
                                      {comm.response_time_ms}ms
                                    </span>
                                  </div>
                                )}

                                {comm.provider_response &&
                                  Object.keys(comm.provider_response).length >
                                    0 && (
                                    <div className="col-span-2">
                                      <span className="font-medium text-muted-foreground">
                                        Provider Response:
                                      </span>
                                      <pre className="mt-2 max-h-48 overflow-auto rounded-lg border border-border bg-muted p-3 text-xs text-muted-foreground">
                                        {JSON.stringify(
                                          comm.provider_response,
                                          null,
                                          2
                                        )}
                                      </pre>
                                    </div>
                                  )}

                                {comm.parent_communication && (
                                  <div className="col-span-2">
                                    <span className="font-medium text-muted-foreground">
                                      Retry of:
                                    </span>{" "}
                                    <span className="font-mono text-xs text-foreground">
                                      {comm.parent_communication}
                                    </span>
                                  </div>
                                )}

                                <div className="col-span-2 pt-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (
                                        comm.status === "sent" ||
                                        comm.status === "delivered"
                                      ) {
                                        setCommToRetry(comm);
                                        setConfirmDialogOpen(true);
                                      } else {
                                        retryMutation.mutate(comm.id);
                                      }
                                    }}
                                    disabled={retryMutation.isPending}
                                  >
                                    {retryMutation.isPending ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Retrying...
                                      </>
                                    ) : (
                                      <>
                                        <RotateCw className="mr-2 h-4 w-4" />
                                        Retry Communication
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          {commsData && commsData.count > 0 && (
            <div className="flex shrink-0 items-center justify-between pt-1">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1}&ndash;
                {Math.min(currentPage * pageSize, commsData.count)} of{" "}
                {commsData.count} results
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((page) => page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Retry</DialogTitle>
            <DialogDescription>
              This communication was already {commToRetry?.status}. Are you sure
              you want to retry it?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setCommToRetry(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (commToRetry) {
                  retryMutation.mutate(commToRetry.id);
                }
                setConfirmDialogOpen(false);
                setCommToRetry(null);
              }}
            >
              Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const CommunicationLogs = () => {
  const [activeTab, setActiveTab] = useState<"patient" | "staff">("patient");

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="shrink-0 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Radio className="h-5 w-5 text-foreground" />
            Communication Logs
          </CardTitle>
        </div>
        <div className="mt-2 flex w-fit gap-1 rounded-lg bg-muted p-0.5">
          <button
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              activeTab === "patient"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={() => setActiveTab("patient")}
          >
            Patient
          </button>
          <button
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              activeTab === "staff"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={() => setActiveTab("staff")}
          >
            Staff
          </button>
        </div>
      </CardHeader>

      <CardContent className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden pt-0">
        {activeTab === "staff" ? (
          <StaffCommunicationLogs />
        ) : (
          <PatientCommunicationLogsPanel />
        )}
      </CardContent>
    </Card>
  );
};
